import { NumberValue, VALUE_TYPE } from "Components/NumberValue";
import { CabinProduct } from "Store/interfaces/products/checkout/riskProducts/houseProducts/CabinProduct";

import {
  F2100AdditionalBuildingType,
  InsuranceEvaluationType,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/AdditionalBuildings/additionalBuildingsInterfaces";
import { useTexts } from "Components/TextContext";
import { formatToLocaleString } from "Formatting/formatNumber";
import React from "react";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";

interface Props {
  product: CabinProduct;
  types: any;
  lang: string;
}
export const AdditionalBuildingsCabinDetails = ({
  product,
  types,
  lang,
}: Props): React.ReactElement => {
  const { getText } = useTexts();
  const [{ details: detailsText }] = useProductTexts(
    getFMSProductName(product.name),
  );

  if (product.additionalBuildings.length === 0) {
    return <span>{detailsText.hasNoAdditionalBuildings[lang]}</span>;
  }

  return (
    <>
      {product.additionalBuildings.map((additionalBuilding, i) => {
        const additionalCabinType =
          types.additionalCabinType[additionalBuilding.type][lang];

        const showIsolationStatus =
          additionalBuilding.type === F2100AdditionalBuildingType.GARAGE ||
          additionalBuilding.type === F2100AdditionalBuildingType.OUTHOUSE;

        const grossArea = (
          <NumberValue
            type={VALUE_TYPE.SQUARE_METERS}
            value={additionalBuilding.grossArea}
          />
        );

        const constructionYear = additionalBuilding.constructionYear;

        const firstRiskIsSet =
          additionalBuilding.insuranceEvaluationType ===
          InsuranceEvaluationType.FIRST_RISK;

        const insuranceEvaluationType = firstRiskIsSet
          ? getText("additionalbuildings.insuranceevaluationtype.first_risk")
          : getText("additionalbuildings.insuranceevaluationtype.full_value");

        const firstRiskSum = firstRiskIsSet && (
          <NumberValue
            type={VALUE_TYPE.NOK}
            value={additionalBuilding?.firstRiskSum}
          />
        );

        const isAnnex =
          additionalBuilding.type === F2100AdditionalBuildingType.ANNEX;

        if (isAnnex) {
          const grossFloorArea = detailsText.areaUnit.grossFloorArea[lang];
          const hasElectricity =
            detailsText[
              additionalBuilding.hasElectricity
                ? "hasElectricity"
                : "hasNoElectricity"
            ][lang];

          const hasTapWater =
            detailsText[
              additionalBuilding.hasTapWater ? "hasTapWater" : "hasNoTapWater"
            ][lang];

          const annexStandard =
            detailsText.annexStandard[additionalBuilding.standard][lang];

          return (
            <div key={`${product.productId}_${i}`}>
              {`${additionalCabinType}, `}
              {grossArea}
              {` ${grossFloorArea}, `}
              {additionalBuilding.livingFloorSpace > 0 && (
                <>
                  <NumberValue
                    type={VALUE_TYPE.SQUARE_METERS}
                    value={additionalBuilding.livingFloorSpace}
                  />
                  {` ${detailsText.areaUnit.usableArea[lang]}`}
                </>
              )}
              {`, ${hasElectricity}, ${hasTapWater}, ${annexStandard}, ${constructionYear}`}
            </div>
          );
        }

        const isolatedInfo =
          detailsText[
            additionalBuilding.isIsolated ? "hasIsolation" : "hasNoIsolation"
          ][lang];

        return (
          <div key={`${product.productId}_${i}`}>
            {`${additionalCabinType}, `}
            {showIsolationStatus && `${isolatedInfo}, `}
            {grossArea}
            {`, ${constructionYear}`}
            {`, ${insuranceEvaluationType}`}
            {firstRiskSum &&
              `: ${formatToLocaleString(
                additionalBuilding.firstRiskSum as number,
              )} kr`}
          </div>
        );
      })}
    </>
  );
};
